/**
 * Dialog / Modal — a Radix Dialog styled as a soft cream sheet with a blurred,
 * warm scrim and a gentle scale-in. The high-level <Dialog> renders title /
 * description / body / footer and a close button; the lower-level Radix parts
 * are also re-exported for custom compositions.
 *
 *   <Dialog
 *     open={open} onOpenChange={setOpen}
 *     title="发布这一章？" description="发布后读者就能看到最新剧情。"
 *     footer={<><Button variant="ghost">再想想</Button><Button>发布</Button></>}
 *   >
 *     正文…
 *   </Dialog>
 */
import { type ComponentPropsWithoutRef, type ReactNode } from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { IconButton } from "./IconButton.js";

export const DialogRoot = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogPortal = RadixDialog.Portal;
export const DialogClose = RadixDialog.Close;

export interface DialogProps
  extends Omit<ComponentPropsWithoutRef<typeof RadixDialog.Root>, "children"> {
  /** Dialog heading (serif). */
  readonly title: ReactNode;
  /** Optional supporting description under the title. */
  readonly description?: ReactNode;
  /** Optional footer (typically action buttons). */
  readonly footer?: ReactNode;
  /** Body content. */
  readonly children?: ReactNode;
  /** Optional trigger element (rendered inside DialogTrigger asChild). */
  readonly trigger?: ReactNode;
  /** Hide the top-right close button. @default false */
  readonly hideClose?: boolean;
}

export function Dialog({
  title,
  description,
  footer,
  children,
  trigger,
  hideClose = false,
  ...rootProps
}: DialogProps) {
  return (
    <RadixDialog.Root {...rootProps}>
      {trigger != null ? (
        <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>
      ) : null}
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="lc-dialog__overlay" />
        <RadixDialog.Content className="lc-dialog__content">
          {!hideClose ? (
            <RadixDialog.Close asChild>
              <span className="lc-dialog__close">
                <IconButton label="关闭" size="sm">
                  <X size={16} aria-hidden="true" />
                </IconButton>
              </span>
            </RadixDialog.Close>
          ) : null}
          <RadixDialog.Title className="lc-dialog__title">{title}</RadixDialog.Title>
          {description != null ? (
            <RadixDialog.Description className="lc-dialog__description">
              {description}
            </RadixDialog.Description>
          ) : null}
          {children != null ? <div className="lc-dialog__body">{children}</div> : null}
          {footer != null ? <div className="lc-dialog__footer">{footer}</div> : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
